import { useEffect, useState } from "react";
import {
  startOfDay,
  isEqual,
  isBefore,
  isAfter,
  startOfToday,
  endOfToday,
  addDays,
  subDays,
  format,
  parse,
} from "date-fns";

import useData from "./useData";
import useErrorTracking from "./useErrorTracking";

const useCall = () => {
  const { getFromServer, executeAction, updateRecord, deleteRecord, sort } =
    useData();
  const { logDataError } = useErrorTracking();

  const [calls, setCalls] = useState(null);

  const getCallById = async (callId) => {
    const call = await getFromServer({
      entityName: "Call",
      pipeline: "full",
      id: callId,
    });

    return call;
  };

  const getCallFullListById = async (callId) => {
    const results = await executeAction({
      entityName: "Call",
      action: "fullList",
      data: { filter: { _id: callId } },
    });

    if (!results.isSuccessful) {
      return null;
    }
    return results.data && results.data.length > 0 ? results.data[0] : null;
  };

  const getCalls = async (filter, sortBy) => {
    // Use fullList action to get enriched joins (project, location, etc.)
    const results = await executeAction({
      entityName: "Call",
      action: "fullList",
      filter,
    });

    const data = results?.isSuccessful ? results.data : [];

    if (!Array.isArray(data)) {
      setCalls([]);
      return;
    }

    if (sortBy) {
      const sorted = sort(data, sortBy.field, sortBy.direction);
      setCalls(sorted);
      return;
    }

    setCalls(data);
  };

  const getDockets = (calls) => {
    let map = new Map();
    calls.forEach((call) => {
      const key = `${call.location._id}-${call.yyyymmdd}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          isSyndicated: call.syndicationId ? true : false,
          project: call.syndicationId ? "syndicated" : call.project.name,
          location: call.location,
          scheduleDate: parse(call.yyyymmdd, "yyyy-MM-dd"),
          repName: call.repName,
          territory: call.territory.name,
          callStatus: call.callStatus,
          calls: [call],
        });
      } else {
        let docket = map.get(key);
        if (call.callStatus === "incomplete") docket.callStatus = "incomplete";
        docket.calls.push(call);
      }
    });

    let dockets = Array.from(map.values());

    return dockets;
  };

  const filterCallsForDate = (date) => {
    if (!date) return [];
    if (!calls || calls.length === 0) return [];
    let dailyCalls = [];

    let yyyymmdd = format(date, "yyyy-MM-dd");

    for (const call of calls) {
      if (call.yyyymmdd === yyyymmdd) {
        dailyCalls.push(call);
      }
    }

    return dailyCalls;
  };

  const callsByLocation = () => {
    if (!calls || calls.length === 0) return [];

    const groupedByLocation = Object.groupBy(calls, (item) => item.locationId);
  };

  const updateCall = async (data, displayMessage = true) => {
    await updateRecord("Call", data, displayMessage);
    await updateCallStatus(data.id);
  };

  const updateCallStatus = async (callId) => {
    try {
      await executeAction({
        entityName: "Call",
        action: "updateCallStatus",
        data: {
          callId,
        },
        displayMessage: false,
      });
    } catch (err) {
      await logDataError(err, "useCall.updateCallStatus.serverAction", {
        callId,
      });

      // Fallback local recalculation if server action is unavailable.
      try {
        const call = await getCallById(callId);
        if (!call) return;

        if (`${call.callStatus || ""}`.trim() === "complete") {
          return;
        }

        if (
          Number(call.payrollLocked ?? 0) === 1 ||
          call.payrollLocked === true ||
          Number(call.isLocked ?? 0) === 1 ||
          call.isLocked === true
        )
          return;

        let callStatus = "complete";

        const todayYyyymmdd = format(startOfToday(), "yyyy-MM-dd");
        const callYyyymmdd =
          typeof call?.yyyymmdd === "string" && call.yyyymmdd
            ? call.yyyymmdd
            : call?.scheduleDate
              ? format(new Date(call.scheduleDate), "yyyy-MM-dd")
              : null;

        if (callYyyymmdd && callYyyymmdd > todayYyyymmdd) {
          callStatus = "future";
        } else {
          const callResultsRaw = await getFromServer({
            entityName: "CallResult",
            filter: { callId: call.id },
          });

          const callResults = Array.isArray(callResultsRaw)
            ? callResultsRaw
            : [];

          const totalCallResults = callResults.length;
          const completedCallResults = callResults.filter(
            (cr) => cr.surveyStatus === "complete",
          ).length;

          if (totalCallResults === 0) {
            callStatus = "incomplete";
          } else if (
            completedCallResults < totalCallResults ||
            !call.callDuration ||
            call.callDuration === 0
          ) {
            callStatus = "incomplete";
          }
        }

        if (callStatus !== call.callStatus) {
          await updateRecord("Call", {
            id: call.id,
            callStatus,
          });
        }
      } catch (fallbackErr) {
        await logDataError(fallbackErr, "useCall.updateCallStatus.fallback", {
          callId,
        });
      }
    }
  };

  const getCallResult = async ({ callId, surveyId }) => {
    const callResult = await executeAction({
      entityName: "CallResult",
      action: "getCallResultForCallAndSurvey",
      data: {
        callId,
        surveyId,
      },
    });

    return callResult;
  };

  const deleteCall = async (callId) => {
    if (!callId) {
      return null;
    }
    const results = await deleteRecord("Call", callId);
    return results;
  };

  const toggleLock = async (callId) => {
    const results = await executeAction({
      entityName: "Call",
      action: "toggleLock",
      id: callId,
    });

    return results;
  };

  const clearCalls = () => {
    setCalls(null);
  };

  return {
    getCalls,
    getCallById,
    getCallFullListById,
    calls,
    callsByLocation,
    filterCallsForDate,
    updateCall,
    clearCalls,
    updateCallStatus,
    deleteCall,
    toggleLock,
    getDockets,
    // getAllIncompleteCalls removed
  };
};

export default useCall;
